import React from 'react';
import Icon from '../../../components/AppIcon';

const MethodologySection = () => {
  const methodologySteps = [
    {
      step: "01",
      title: "AI Independence Assessment",
      description: "Comprehensive audit of current AI dependencies, vendor lock-in risks, and internal capability gaps.",
      deliverables: ["Dependency Risk Report", "Capability Gap Analysis", "Independence Roadmap"],
      duration: "2-4 weeks",
      icon: "Search"
    },
    {
      step: "02",
      title: "Open-Source Strategy Design",
      description: "Custom strategy for transitioning to open-source AI solutions while maintaining business continuity.",
      deliverables: ["Technology Stack Blueprint", "Migration Strategy", "Risk Mitigation Plan"],
      duration: "3-6 weeks",
      icon: "Map"
    },
    {
      step: "03",
      title: "Internal Capability Building",
      description: "Develop internal AI expertise through training, mentoring, and hands-on implementation support.",
      deliverables: ["Training Programs", "Mentorship Framework", "Knowledge Transfer"],
      duration: "8-12 weeks",
      icon: "Users"
    },
    {
      step: "04",
      title: "Sustainable Implementation",
      description: "Deploy open-source AI solutions with full internal ownership and ongoing optimization capabilities.",
      deliverables: ["Production Deployment", "Monitoring Systems", "Optimization Framework"],
      duration: "6-10 weeks",
      icon: "Rocket"
    }
  ];

  const differentiators = [
    {
      traditional: "Quick fixes that create new dependencies",
      aiCleanCore: "Sustainable solutions that eliminate dependencies",
      icon: "Target"
    },
    {
      traditional: "Vendor-specific implementations",
      aiCleanCore: "Vendor-agnostic, portable solutions",
      icon: "Unlock"
    },
    {
      traditional: "External expertise dependency",
      aiCleanCore: "Internal capability development",
      icon: "Building"
    },
    {
      traditional: "Proprietary black-box solutions",
      aiCleanCore: "Transparent, customizable open-source",
      icon: "Eye"
    }
  ];

  return (
    <section className="py-20 lg:py-32 bg-brand-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-brand-primary/10 rounded-full px-4 py-2 mb-6">
            <Icon name="Cog" size={16} className="text-brand-primary" />
            <span className="text-sm font-medium text-brand-primary">Our Methodology</span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-6">
            The AI Clean Core
            <span className="block text-brand-primary">Methodology</span>
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Our proven 4-phase approach transforms organizations from vendor-dependent to AI-independent, with sustainable results that compound over time.
          </p>
        </div>

        {/* Methodology Steps */}
        <div className="space-y-8 mb-20">
          {methodologySteps?.map((step, index) => (
            <div key={index} className="bg-white rounded-xl shadow-executive p-8 hover:shadow-executive-lg transition-all duration-300">
              <div className="grid lg:grid-cols-4 gap-8 items-start">
                <div className="lg:col-span-1">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-brand-primary text-white rounded-lg flex items-center justify-center font-bold">
                      {step?.step}
                    </div>
                    <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={step?.icon} size={24} className="text-brand-primary" />
                    </div>
                  </div>
                  
                  <div className="text-sm text-brand-primary font-medium mb-2">
                    Duration: {step?.duration}
                  </div>
                </div>
                
                <div className="lg:col-span-2">
                  <h3 className="text-xl font-bold text-text-primary mb-3">
                    {step?.title}
                  </h3>
                  <p className="text-text-secondary leading-relaxed">
                    {step?.description}
                  </p>
                </div>
                
                <div className="lg:col-span-1">
                  <h4 className="font-semibold text-text-primary mb-3">Key Deliverables</h4>
                  <ul className="space-y-2">
                    {step?.deliverables?.map((deliverable, deliverableIndex) => (
                      <li key={deliverableIndex} className="flex items-center space-x-2">
                        <Icon name="CheckCircle" size={16} className="text-brand-accent flex-shrink-0" />
                        <span className="text-sm text-text-secondary">{deliverable}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Differentiators */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-executive">
          <div className="text-center mb-12">
            <h3 className="text-2xl lg:text-3xl font-bold text-text-primary mb-4">
              How We're Different
            </h3>
            <p className="text-lg text-text-secondary">
              Traditional consulting creates dependencies. We eliminate them.
            </p>
          </div>
          
          <div className="space-y-6">
            {differentiators?.map((diff, index) => (
              <div key={index} className="grid md:grid-cols-2 gap-8 items-center p-6 bg-brand-surface rounded-xl">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <Icon name="X" size={20} className="text-red-600" />
                  </div>
                  <div>
                    <div className="text-sm text-red-600 font-medium mb-1">Traditional Approach</div>
                    <div className="text-text-secondary">{diff?.traditional}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Icon name={diff?.icon} size={20} className="text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm text-green-600 font-medium mb-1">AI Clean Core Way</div>
                    <div className="text-text-primary font-medium">{diff?.aiCleanCore}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MethodologySection;